import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSearch,
  faUser,
  faCheckSquare,
  faSquare,
} from "@fortawesome/free-solid-svg-icons";
import { getToken } from "../../function/token";
import domain from "../../../reusecomponent/domain";
import { ErrorToastContainer } from "../../function/CustomFunction";

export default function AssignChatContactsModal({
  isOpen,
  onClose,
  onAssign,
  agentId,
  preselectedNumbers = [],
}) {
  const [loading, setLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set(preselectedNumbers));

  useEffect(() => {
    if (!isOpen) return;
    setSelected(new Set(preselectedNumbers));
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchContacts = async () => {
    if (!agentId) {
      console.error("No agent ID provided");
      setContacts([]);
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const headers = new Headers({
        Authorization: `Bearer ${token}`,
      });

      console.log("Agent id selected: ", agentId);
      // Use existing WhatsApp chat contacts API used in Whatsappchat
      const response = await fetch(`${domain()}/assign-chat/${btoa(agentId)}`, {
        method: "GET",
        headers,
        redirect: "follow",
      });
      const result = await response.json();
      console.log("API Response (raw):", result);

      // Handle the response structure - data is base64 encoded
      let decoded = { data: [] };
      if (result?.data) {
        try {
          // Decode the base64 encoded data
          const decodedString = atob(result.data);
          decoded = JSON.parse(decodedString);
          console.log("Decoded data:", decoded);
        } catch (e) {
          console.error("Error decoding base64 data:", e);
          decoded = { data: [] };
        }
      }

      const list = Array.isArray(decoded.data) ? decoded.data : [];
      console.log("Contacts list:", list);

      const normalized = list.map((c, idx) => ({
        id: c.contact_id || idx + 1,
        name: c.name || c.contact_name || "Unknown",
        number: c.contact_id || `contact_${idx}`, // Generate number if missing
        contact_id: c.contact_id,
        contact_name: c.contact_name,
        profile_picture: c.profile_picture,
        is_assign: c.is_assign || false,
      }));

      console.log("Normalized contacts:", normalized);
      setContacts(normalized);

      // Pre-select contacts that are already assigned (is_assign: true)
      const preAssigned = normalized
        .filter((c) => c.is_assign)
        .map((c) => c.number);
      console.log("Pre-assigned contacts:", preAssigned);
      setSelected(new Set(preAssigned));
    } catch (e) {
      ErrorToastContainer("Failed to fetch contacts");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    if (!search) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) || (c.number || "").includes(q)
    );
  }, [contacts, search]);

  const allVisibleSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every((c) => selected.has(c.number));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const next = new Set(selected);
      filteredContacts.forEach((c) => next.delete(c.number));
      setSelected(next);
    } else {
      const next = new Set(selected);
      filteredContacts.forEach((c) => next.add(c.number));
      setSelected(next);
    }
  };

  const toggleOne = (num) => {
    const next = new Set(selected);
    if (next.has(num)) next.delete(num);
    else next.add(num);
    setSelected(next);
  };

  const handleAssign = async () => {
    const numbers = Array.from(selected);
    console.log("Selected numbers:", numbers);
    console.log("Total contacts:", contacts.length);

    // if (numbers.length === 0) {
    //   ErrorToastContainer("Please select at least one contact");
    //   return;
    // }

    setAssignLoading(true);

    try {
      // Determine assignment type
      let assignmentType;
      if (numbers.length == 0) {
        assignmentType = "remove";
      } else {
        assignmentType =
          numbers.length === contacts.length ? "all" : "specific";
      }

      console.log("Assignment type:", assignmentType);

      // Create detailed assignment data with contact information
      const assignmentData = numbers.map((number) => {
        const contact = contacts.find((c) => c.number === number);
        return {
          number: number,
          contact_id: contact?.contact_id,
          name: contact?.name,
          contact_name: contact?.contact_name,
          is_assign: true, // Mark as assigned
        };
      });

      console.log("Assignment data:", assignmentData);

      await onAssign({
        added_type: assignmentType,
        mobile_number: numbers,
        assignment_details: assignmentData,
      });
    } catch (error) {
      console.error("Assignment failed:", error);
    } finally {
      setAssignLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="blur-background d-flex justify-content-center align-items-center"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.18)",
        zIndex: 1050,
        padding: "8px",
      }}
    >
      <div
        className="card shadow-lg border-0"
        style={{
          width: "100%",
          maxWidth: "640px",
          borderRadius: "16px",
          maxHeight: "95vh",
          overflow: "hidden",
          margin: "0 auto",
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between"
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #eee",
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: "18px",
              color: "#1f2937",
            }}
          >
            Assign Chat Contacts
          </div>
          <button
            className="btn btn-light d-flex align-items-center justify-content-center"
            style={{
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              padding: 0,
              border: "none",
              background: "#f3f4f6",
            }}
            onClick={onClose}
            disabled={assignLoading}
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: "14px" }} />
          </button>
        </div>

        <div
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #f2f2f2",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              border: "1px solid #e3e6ea",
              borderRadius: "8px",
              background: "#fff",
              padding: "8px 12px",
            }}
          >
            <FontAwesomeIcon
              icon={faSearch}
              style={{
                color: "#9ca3af",
                marginRight: "8px",
                fontSize: "14px",
              }}
            />
            <input
              type="text"
              placeholder="Search contacts by name or number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: "14px",
                width: "100%",
                padding: "4px 0",
              }}
            />
          </div>
          <div className="form-check mt-2" style={{ marginTop: "12px" }}>
            <input
              className="form-check-input"
              type="checkbox"
              id="selectAllVisible"
              checked={allVisibleSelected}
              onChange={toggleSelectAllVisible}
            />
            <label className="form-check-label" htmlFor="selectAllVisible">
              Select all in current list
            </label>
          </div>
        </div>

        <div
          style={{
            padding: 0,
            overflowY: "auto",
            maxHeight: "60vh",
            minHeight: "200px",
          }}
        >
          {loading ? (
            <div className="p-4 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading contacts...</div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="p-4 text-center text-muted">
              <FontAwesomeIcon icon={faUser} size="2x" className="mb-2" />
              <div>No contacts found.</div>
            </div>
          ) : (
            <ul className="list-group list-group-flush">
              {filteredContacts.map((c) => (
                <li
                  key={`${c.id}-${c.number}`}
                  className="list-group-item d-flex align-items-center justify-content-between"
                  style={{
                    padding: "12px 16px",
                    borderLeft: "none",
                    borderRight: "none",
                  }}
                >
                  <div
                    className="d-flex align-items-center flex-grow-1"
                    style={{ gap: "12px", minWidth: 0 }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        background: "#f2f4f7",
                        color: "#6b7280",
                        overflow: "hidden",
                      }}
                    >
                      {c.profile_picture ? (
                        <img
                          src={`https://api.campaigningtool.com/contact_picture/${c.profile_picture}`}
                          alt={c.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : (
                        <FontAwesomeIcon icon={faUser} />
                      )}
                      <FontAwesomeIcon
                        icon={faUser}
                        style={{ display: c.profile_picture ? "none" : "flex" }}
                      />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#1f2937",
                          marginBottom: "2px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.name}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          marginBottom: "2px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {c.contact_name && <span>{c.contact_name} • </span>}
                        {c.number}
                      </div>
                      {c.is_assign && (
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#059669",
                            fontWeight: 500,
                            marginTop: "2px",
                          }}
                        >
                          Currently Assigned
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    className="btn btn-sm d-flex align-items-center justify-content-center flex-shrink-0"
                    onClick={() => toggleOne(c.number)}
                    style={{
                      width: "36px",
                      height: "36px",
                      border: "none",
                      background: "transparent",
                      padding: 0,
                    }}
                  >
                    <FontAwesomeIcon
                      icon={selected.has(c.number) ? faCheckSquare : faSquare}
                      style={{
                        color: selected.has(c.number) ? "#059669" : "#6b7280",
                      }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className="d-flex flex-column flex-sm-row justify-content-end"
          style={{
            gap: "12px",
            padding: "12px 16px",
            borderTop: "1px solid #eee",
            background: "#fafafa",
          }}
        >
          <button
            className="btn btn-light order-2 order-sm-1"
            onClick={onClose}
            disabled={assignLoading}
            style={{
              padding: "10px 20px",
              fontWeight: 500,
              border: "1px solid #d1d5db",
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary order-1 order-sm-2"
            onClick={handleAssign}
            disabled={assignLoading}
            style={{
              padding: "10px 20px",
              fontWeight: 500,
              background: "#059669",
              border: "none",
            }}
          >
            {assignLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Assigning...
              </>
            ) : (
              "Assign"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
